import { Icon, XStack, YStack } from '@frontend/web-react';
import { concatClasses } from '@frontend/web-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { StackCard } from './StackCard';
import type { CardItem, CardsStackProps, CardsStackRenderItemProps } from './types';
import { useCardStyle } from './useCardStyle';
import { useCardsStack } from './useCardsStack';
import { useVisibleCards } from './useVisibleCards';

const DEFAULT_VISIBLE_CARDS = 1;
const DEFAULT_STACK_SCALE = 0.8;
const DEFAULT_CARD_HEIGHT = 180;
const DEFAULT_CARD_HEIGHT_MOBILE = 220;
const DEFAULT_ANIMATION_DURATION = 500;

const MOBILE_BREAKPOINT = 768;

type CardWrapperProps<T extends CardItem> = {
  animationDuration: number;
  cardHeight: number;
  containerWidth: number;
  isActive: boolean;
  isMobile: boolean;
  item: T;
  onDismiss?: () => void;
  onStartTask?: () => void;
  renderItem: (props: CardsStackRenderItemProps<T>) => React.ReactNode;
  stackPosition: number;
  stackScale: number;
  visibleCardsCount: number;
};

const CardWrapper = <T extends CardItem>({
  animationDuration,
  cardHeight,
  containerWidth,
  isActive,
  isMobile,
  item,
  onDismiss,
  onStartTask,
  renderItem,
  stackPosition,
  stackScale,
  visibleCardsCount,
}: CardWrapperProps<T>) => {
  const style = useCardStyle({
    animationDuration,
    cardHeight,
    containerWidth,
    isActive,
    isMobile,
    stackPosition,
    stackScale,
    visibleCardsCount,
  });

  return (
    <div className="absolute" style={style}>
      <div className="size-full">{renderItem({ isActive, isMobile, item, onDismiss, onStartTask })}</div>
    </div>
  );
};

type NavigationButtonProps = {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
};

const NavigationButton = ({ direction, disabled, onClick }: NavigationButtonProps) => {
  const label = direction === 'left' ? 'Previous card' : 'Next card';
  return (
    <button
      aria-label={label}
      className={concatClasses(
        'flex size-[48px] items-center justify-center rounded-full border border-neutral-200 bg-white transition-all',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-neutral-100',
      )}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      <Icon
        color={disabled ? 'tertiary' : 'primary'}
        name={direction === 'left' ? 'IconChevronLeft' : 'IconChevronRight'}
        size="md"
      />
    </button>
  );
};

export const CardsStack = <T extends CardItem>({
  animationDuration = DEFAULT_ANIMATION_DURATION,
  cardHeight: cardHeightProp,
  className,
  data,
  initialIndex = 0,
  loop = false,
  onDismiss,
  onNextCard,
  onPreviousCard,
  onStartTask,
  renderItem,
  showNavigation = true,
  stackScale = DEFAULT_STACK_SCALE,
  testId,
  visibleCardsCount = DEFAULT_VISIBLE_CARDS,
}: CardsStackProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      setWindowWidth(window.innerWidth);
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const isMobile = windowWidth > 0 && windowWidth < MOBILE_BREAKPOINT;
  const cardHeight = cardHeightProp ?? (isMobile ? DEFAULT_CARD_HEIGHT_MOBILE : DEFAULT_CARD_HEIGHT);

  const { activeIndex, canGoNext, canGoPrevious, goToNext, goToPrevious } = useCardsStack({
    initialIndex,
    loop,
    onIndexChange: (newIndex, oldIndex) => {
      if (newIndex > oldIndex) {
        onNextCard?.(newIndex);
      } else {
        onPreviousCard?.(newIndex);
      }
    },
    totalCards: data.length,
  });

  const visibleCards = useVisibleCards({ activeIndex, data, visibleCardsCount });

  const handleDismiss = useCallback(
    (item: T, index: number) => {
      onDismiss?.(item, index);
    },
    [onDismiss],
  );

  const handleStartTask = useCallback(
    (item: T, index: number) => {
      onStartTask?.(item, index);
    },
    [onStartTask],
  );

  const defaultRenderItem = useCallback(
    ({
      isActive,
      isMobile: mobile,
      item,
      onDismiss: dismissFn,
      onStartTask: startTaskFn,
    }: CardsStackRenderItemProps<T>) => {
      const content = 'content' in item && typeof item.content === 'string' ? item.content : '';
      return (
        <StackCard
          content={content}
          isActive={isActive}
          isMobile={mobile}
          onAction={startTaskFn}
          onDismiss={dismissFn}
        />
      );
    },
    [],
  );

  const renderCard = renderItem ?? defaultRenderItem;

  if (data.length === 0) {
    return null;
  }

  const showSideNav = showNavigation && data.length > 1 && !isMobile;
  const showTopNav = showNavigation && data.length > 1 && isMobile;

  return (
    <YStack className={concatClasses('w-full', className)} data-testid={testId}>
      {showTopNav ? (
        <XStack className="mb-md items-center justify-end gap-md">
          <NavigationButton direction="left" disabled={!canGoPrevious} onClick={goToPrevious} />
          <NavigationButton direction="right" disabled={!canGoNext} onClick={goToNext} />
        </XStack>
      ) : null}

      <XStack className="w-full items-center gap-lg">
        {showSideNav ? <NavigationButton direction="left" disabled={!canGoPrevious} onClick={goToPrevious} /> : null}

        <YStack
          className={concatClasses('relative flex-1', isMobile ? 'overflow-visible' : 'overflow-hidden')}
          ref={containerRef}
          style={{ height: cardHeight + 32 }}
        >
          {containerWidth > 0 &&
            visibleCards.map(({ index, item, stackPosition }) => (
              <CardWrapper
                animationDuration={animationDuration}
                cardHeight={cardHeight}
                containerWidth={containerWidth}
                isActive={index === activeIndex}
                isMobile={isMobile}
                item={item}
                key={item.id}
                onDismiss={() => handleDismiss(item, index)}
                onStartTask={() => handleStartTask(item, index)}
                renderItem={renderCard}
                stackPosition={stackPosition}
                stackScale={stackScale}
                visibleCardsCount={visibleCardsCount}
              />
            ))}
        </YStack>

        {showSideNav ? <NavigationButton direction="right" disabled={!canGoNext} onClick={goToNext} /> : null}
      </XStack>
    </YStack>
  );
};
